import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Components
import Header from './components/Header';
import Navigation from './components/Navigation';
import Dashboard from './pages/Dashboard';
import TransactionList from './pages/TransactionList';
import AddTransaction from './pages/AddTransaction';
import Statistics from './pages/Statistics';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

function App() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch transactions from API
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/transactions`);
      const result = await response.json();
      
      if (result.success) {
        setTransactions(result.data);
        setError(null);
      } else {
        setError(result.error || 'データの取得に失敗しました');
      }
    } catch (err) {
      setError('ネットワークエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  // Add new transaction
  const addTransaction = async (transactionData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transactionData),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setTransactions(prev => [result.data, ...prev]);
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      return { success: false, error: 'ネットワークエラーが発生しました' };
    }
  };

  // Update transaction
  const updateTransaction = async (id, transactionData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/transactions/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(transactionData),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setTransactions(prev => 
          prev.map(t => t.id === id ? result.data : t)
        );
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      return { success: false, error: 'ネットワークエラーが発生しました' };
    }
  };

  // Delete transaction
  const deleteTransaction = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/transactions/${id}`, {
        method: 'DELETE',
      });
      
      const result = await response.json();
      
      if (result.success) {
        setTransactions(prev => prev.filter(t => t.id !== id));
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (err) {
      return { success: false, error: 'ネットワークエラーが発生しました' };
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const appProps = {
    transactions,
    loading,
    error,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    refreshTransactions: fetchTransactions,
    apiBaseUrl: API_BASE_URL
  };

  return (
    <Router>
      <div className="app">
        <Header />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard {...appProps} />} />
            <Route path="/transactions" element={<TransactionList {...appProps} />} />
            <Route path="/add" element={<AddTransaction {...appProps} />} />
            <Route path="/statistics" element={<Statistics {...appProps} />} />
          </Routes>
        </main>
        <Navigation />
      </div>
    </Router>
  );
}

export default App;